import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import ActionMenu from "@/v2-backoffice-client/components/actionMenu"
import CustomerEditDialog from "@/v2-backoffice-client/customer/edit/CustomerEditDialog"
import CustomerCompanyLinkDialog from "@/v2-backoffice-client/customer/company/CustomerCompanyLinkDialog"
import MigrationCenterDialog from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterDialog"
import { useCustomerMultiSessionMutation } from "@/v2-backoffice-client/customer/multiSession/customer.multiSession.mutation"
import { buildMixpanelBoardUrl } from "@/v2-backoffice-client/analytics/analytics.utils"
import { buildCustomerViewLink } from "@/v2-backoffice-client/customer/customer.links"
import {
  FENERUM_ACCOUNT_BASE_URL,
  HUBSPOT_BASE_URL,
} from "@/v2-backoffice-client/constants"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"

export type CustomerActionMenuProps = {
  customer: CustomerFragment
}

// The customers-board row menu: the panel toolbar's actions without opening
// the panel — update the identity fields, jump to the commercial story, the
// three externals (Mixpanel / HubSpot / Fenerum), manage the HubSpot link
// and the multi-session exemption. There is deliberately no delete: sync-born
// customers heal back within the hour, and app-born ones carry licensing
// history.
function CustomerActionMenu(props: CustomerActionMenuProps) {
  const { customer } = props
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isLinkOpen, setIsLinkOpen] = useState(false)
  const [isMigrateOpen, setIsMigrateOpen] = useState(false)

  const [setMultiSession, { loading: isMultiSessionLoading, error }] =
    useCustomerMultiSessionMutation()
  errorToast(error, { title: "Failed to update multi-session exemption" })

  return (
    <>
      <ActionMenu>
        <ActionMenu.Trigger />

        <ActionMenu.Content>
          <ActionMenu.Item
            iconSrc="edit"
            data-feature-code="UI_BackofficeCustomers_Row_Update"
            onSelect={(e) => {
              e.preventDefault()
              setIsEditOpen(true)
            }}
          >
            Update Customer
          </ActionMenu.Item>

          {/* Straight to the commercial story — licenses, collections,
              billing — where Grant license lives. Panel deep links need the
              HubSpot id (the sheet is keyed by it). */}
          {customer.ExternalCustomerId && (
            <ActionMenu.LinkItem
              iconSrc="license"
              to={buildCustomerViewLink(customer.ExternalCustomerId, "billing")}
            >
              Open Licensing
            </ActionMenu.LinkItem>
          )}

          {/* The Migration Center scoped to this customer's teams (FUS-927)
              — same center as the toolbars, so sends never drift. */}
          <ActionMenu.Item
            iconSrc="key"
            disabled={customer.Tenants.length === 0}
            data-feature-code="UI_BackofficeCustomers_Row_FusionLogin"
            onSelect={(e) => {
              e.preventDefault()
              setIsMigrateOpen(true)
            }}
          >
            Fusion Login Migration
          </ActionMenu.Item>

          <ActionMenu.Separator />

          <ActionMenu.LinkItem
            iconSrc="mixpanel"
            external
            to={buildMixpanelBoardUrl({
              resourceType: "event",
              propertyName: "Customer.id",
              propertyType: "number",
              filterOperator: "equals",
              filterValue: String(customer.Id),
            })}
          >
            Open in Mixpanel
          </ActionMenu.LinkItem>

          {customer.ExternalCustomerId && (
            <ActionMenu.LinkItem
              iconSrc="hubspot"
              external
              to={`${HUBSPOT_BASE_URL}/${customer.ExternalCustomerId}`}
            >
              Open in HubSpot
            </ActionMenu.LinkItem>
          )}

          <ActionMenu.Item
            iconSrc="link"
            data-feature-code="UI_BackofficeCustomers_Row_LinkCompany"
            onSelect={(e) => {
              e.preventDefault()
              setIsLinkOpen(true)
            }}
          >
            {customer.Company
              ? "View / Unlink Company"
              : "Link HubSpot Company"}
          </ActionMenu.Item>

          <ActionMenu.LinkItem
            iconSrc="bank"
            disabled={!customer.BillingAccountUuid}
            external
            to={
              customer.BillingAccountUuid
                ? `${FENERUM_ACCOUNT_BASE_URL}/${customer.BillingAccountUuid}/`
                : ""
            }
          >
            Open in Fenerum
          </ActionMenu.LinkItem>

          <ActionMenu.Separator />
          <ActionMenu.Label>Multi-Session</ActionMenu.Label>

          <ActionMenu.ConfirmItem
            iconSrc="unlock"
            variant="success"
            content="Exempt every user of this customer from the single-active-session limit."
            confirmDescription="Allow multi-session for all users of this customer?"
            confirmVariant="success"
            isLoading={isMultiSessionLoading}
            data-feature-code="UI_BackofficeCustomers_Row_MultiSessionAllow"
            onConfirm={() => {
              setMultiSession({
                variables: { customerId: customer.Id, allowed: true },
              })
            }}
          >
            Allow for All Users
          </ActionMenu.ConfirmItem>

          <ActionMenu.ConfirmItem
            iconSrc="lock"
            variant="warning"
            content="Re-subject every user of this customer to the single-active-session limit."
            confirmDescription="Disallow multi-session for all users of this customer?"
            confirmVariant="warning"
            isLoading={isMultiSessionLoading}
            data-feature-code="UI_BackofficeCustomers_Row_MultiSessionDisallow"
            onConfirm={() => {
              setMultiSession({
                variables: { customerId: customer.Id, allowed: false },
              })
            }}
          >
            Disallow for All Users
          </ActionMenu.ConfirmItem>
        </ActionMenu.Content>
      </ActionMenu>

      {/* Mounted per open so the forms re-seed from the row every time. */}
      {isEditOpen && (
        <CustomerEditDialog
          customer={customer}
          open={isEditOpen}
          onOpenChange={setIsEditOpen}
        />
      )}

      {isLinkOpen && (
        <CustomerCompanyLinkDialog
          customer={customer}
          open={isLinkOpen}
          onOpenChange={setIsLinkOpen}
        />
      )}

      {isMigrateOpen && (
        <MigrationCenterDialog
          open={isMigrateOpen}
          onOpenChange={setIsMigrateOpen}
          selection={{ kind: "customers", customers: [customer] }}
        />
      )}
    </>
  )
}

export default CustomerActionMenu
